/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v3";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import * as types from "../../types/primitives.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import {
  AnimaticGauge,
  AnimaticGauge$inboundSchema,
  AnimaticGauge$Outbound,
  AnimaticGauge$outboundSchema,
} from "./animaticgauge.js";
import {
  AnimaticLabel,
  AnimaticLabel$inboundSchema,
  AnimaticLabel$Outbound,
  AnimaticLabel$outboundSchema,
} from "./animaticlabel.js";
import {
  AnimaticPrimitive,
  AnimaticPrimitive$inboundSchema,
  AnimaticPrimitive$Outbound,
  AnimaticPrimitive$outboundSchema,
} from "./animaticprimitive.js";
import {
  AnimaticShelf,
  AnimaticShelf$inboundSchema,
  AnimaticShelf$Outbound,
  AnimaticShelf$outboundSchema,
} from "./animaticshelf.js";

/**
 * Defines the configuration of an animatic. The Animatic is made up of a primitive component, and optional labels, gauges, and a shelf.
 */
export type Animatic = {
  /**
   * The gauges of the animatic.
   */
  gauges?: Array<AnimaticGauge> | undefined;
  /**
   * The labels of the animatic.
   */
  labels?: Array<AnimaticLabel> | undefined;
  /**
   * The misconfigured reasons of the animatic.
   */
  misconfiguredReasons?: Array<string> | undefined;
  /**
   * Defines a primitive animatic component. Primitives are the base of an animatic.
   */
  primitive: AnimaticPrimitive;
  /**
   * Specifies details for a shelf that is displayed with an Animatic.
   */
  shelf?: AnimaticShelf | undefined;
};

/** @internal */
export const Animatic$inboundSchema: z.ZodType<
  Animatic,
  z.ZodTypeDef,
  unknown
> = z.object({
  gauges: types.optional(z.array(AnimaticGauge$inboundSchema)),
  labels: types.optional(z.array(AnimaticLabel$inboundSchema)),
  misconfigured_reasons: types.optional(z.array(types.string())),
  primitive: AnimaticPrimitive$inboundSchema,
  shelf: types.optional(AnimaticShelf$inboundSchema),
}).transform((v) => {
  return remap$(v, {
    "misconfigured_reasons": "misconfiguredReasons",
  });
});
/** @internal */
export type Animatic$Outbound = {
  gauges?: Array<AnimaticGauge$Outbound> | undefined;
  labels?: Array<AnimaticLabel$Outbound> | undefined;
  misconfigured_reasons?: Array<string> | undefined;
  primitive: AnimaticPrimitive$Outbound;
  shelf?: AnimaticShelf$Outbound | undefined;
};

/** @internal */
export const Animatic$outboundSchema: z.ZodType<
  Animatic$Outbound,
  z.ZodTypeDef,
  Animatic
> = z.object({
  gauges: z.array(AnimaticGauge$outboundSchema).optional(),
  labels: z.array(AnimaticLabel$outboundSchema).optional(),
  misconfiguredReasons: z.array(z.string()).optional(),
  primitive: AnimaticPrimitive$outboundSchema,
  shelf: AnimaticShelf$outboundSchema.optional(),
}).transform((v) => {
  return remap$(v, {
    misconfiguredReasons: "misconfigured_reasons",
  });
});

export function animaticToJSON(animatic: Animatic): string {
  return JSON.stringify(Animatic$outboundSchema.parse(animatic));
}
export function animaticFromJSON(
  jsonString: string,
): SafeParseResult<Animatic, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => Animatic$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'Animatic' from JSON`,
  );
}
